import React from 'react'
import { Users, Building2, User, Landmark, PieChart } from 'lucide-react'
import { formatPercent } from '../utils/format'

const BIAS_COLOR = {
  buying: 'var(--onds-bull)',
  accumulating: 'var(--onds-bull)',
  selling: 'var(--onds-bear)',
  distributing: 'var(--onds-bear)',
  mixed: 'var(--onds-caution)',
  neutral: 'var(--onds-text-dim)',
  stable: 'var(--onds-text-dim)',
}

const TYPE_ICON = {
  insider: User,
  institution: Landmark,
  etf: PieChart,
  'active fund': Building2,
}

const biasColor = (v) => BIAS_COLOR[(v ?? '').toLowerCase()] ?? 'var(--onds-text-dim)'

function Metric({ label, value, color }) {
  return (
    <div className="rounded-xl p-3 border" style={{ backgroundColor: 'var(--onds-surface)', borderColor: 'var(--onds-border)' }}>
      <p className="text-[10px] uppercase tracking-wider text-[var(--onds-text-dim)] mb-1">{label}</p>
      <p className="text-base font-bold capitalize" style={{ color: color ?? 'var(--onds-text)' }}>{value}</p>
    </div>
  )
}

export default function InsidersPanel({ insiders }) {
  if (!insiders || (!insiders.summary && !(insiders.transactions?.length))) return null

  return (
    <div className="onds-card p-5 md:p-6">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-5 w-5" style={{ color: 'var(--onds-accent)' }} />
        <h2 className="text-lg font-bold">Insider &amp; Institutional Activity</h2>
        <span className="text-sm text-[var(--onds-text-dim)]">who is buying and selling</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Metric label="Insider bias" value={insiders.insiderBias || '—'} color={biasColor(insiders.insiderBias)} />
        <Metric label="Institutional trend" value={insiders.institutionalTrend || '—'} color={biasColor(insiders.institutionalTrend)} />
        <Metric label="Institutional own." value={insiders.institutionalOwnershipPercent != null ? formatPercent(insiders.institutionalOwnershipPercent, 0) : '—'} />
      </div>

      {insiders.summary && (
        <p className="text-sm text-[var(--onds-text-dim)] leading-relaxed mb-4">{insiders.summary}</p>
      )}

      <div className="flex flex-col gap-2">
        {(insiders.transactions ?? []).map((t, i) => {
          const Icon = TYPE_ICON[(t.type ?? '').toLowerCase()] ?? Building2
          const isBuy = (t.action ?? '').toLowerCase() === 'buy'
          const actionColor = isBuy ? 'var(--onds-bull)' : 'var(--onds-bear)'
          return (
            <div key={i} className="flex items-start gap-3 rounded-xl p-3 border" style={{ backgroundColor: 'var(--onds-surface)', borderColor: 'var(--onds-border)' }}>
              <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--onds-text-dim)' }} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold leading-tight">{t.name}</span>
                  {t.type && <span className="text-[10px] uppercase tracking-wider text-[var(--onds-text-dim)]">{t.type}</span>}
                </div>
                {t.note && <p className="text-sm text-[var(--onds-text-dim)] leading-relaxed mt-0.5">{t.note}</p>}
              </div>
              <div className="text-right shrink-0">
                {t.action && (
                  <span className="text-sm font-bold" style={{ color: actionColor }}>{t.action}</span>
                )}
                {t.size && <p className="text-xs text-[var(--onds-text-dim)] tabular-nums">{t.size}</p>}
                {t.date && <p className="text-[11px] text-[var(--onds-text-dim)]">{t.date}</p>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
